'use strict';
// One memory-only instance per authenticated runtime. Display offers only:
// checkout still obtains and validates its own authoritative offer.
const PERMANENT=[2,3,5,10].map(n=>({skuId:'permanent-'+n}));
const copy=value=>JSON.parse(JSON.stringify(value));
const key=value=>value.skuId+':'+(value.activityId||'');
const error=code=>Object.assign(Error(code),{code});
function createPricePreload({readOffers,readActivity,host,now=Date.now,setTimer=setTimeout,clearTimer=clearTimeout}){
 if(typeof readOffers!=='function')throw error('PRICE_PRELOAD_DEPENDENCIES');
 const cache=new Map(),jobs=new Map(),queue=[];
 let disposed=false,running=0,timer=null,cycle=null,activity=null,started=false;
 const hidden=()=>host?.document?.visibilityState==='hidden';
 const allowed=s=>s&&/^(permanent-(2|3|5|10)|limited-step-[1-6])$/.test(s.skuId||'')&&
  (s.skuId.startsWith('permanent-')?s.activityId==null:activity&&s.activityId===activity.activityId&&s.skuId==='limited-step-'+activity.nextStep&&now()<activity.endAt);
 const cancel=()=>{if(timer!==null)clearTimer(timer);timer=null;};
 function validEntry(s){const entry=cache.get(key(s));if(entry&&entry.until>now()&&allowed(s))return entry;cache.delete(key(s));return null;}
 function setActivity(value){
  const valid=value&&value.enabled===true&&value.purchasingEnabled===true&&value.completed===false&&
   typeof value.activityId==='string'&&/^[A-Za-z0-9][A-Za-z0-9._:-]{0,179}$/.test(value.activityId)&&
   Number.isSafeInteger(value.startAt)&&Number.isSafeInteger(value.endAt)&&value.startAt<=now()&&now()<value.endAt&&
   Number.isSafeInteger(value.nextStep)&&value.nextStep>=1&&value.nextStep<=6;
  activity=valid?{activityId:value.activityId,nextStep:value.nextStep,endAt:value.endAt}:null;
  for(const k of cache.keys())if(k.startsWith('limited-')&&(!activity||k!=='limited-step-'+activity.nextStep+':'+activity.activityId))cache.delete(k);
 }
 function drain(){
  while(!disposed&&!hidden()&&running<2&&queue.length){
   const job=queue.shift();
   if(!allowed(job.selection)){jobs.delete(job.key);job.reject(error('PRICE_SELECTION_NOT_CURRENT'));continue;}
   running++;
   Promise.resolve().then(()=>disposed?Promise.reject(error('PRICE_PRELOAD_DISPOSED')):readOffers({...job.selection})).then(value=>{
    if(disposed)throw error('PRICE_PRELOAD_DISPOSED');
    if(!allowed(job.selection))throw error('PRICE_SELECTION_NOT_CURRENT');
    if(!value||value.schema!==1||value.skuId!==job.selection.skuId||(value.activityId||null)!==(job.selection.activityId||null)||!Array.isArray(value.offers)||value.offers.length>3)throw error('INVALID_PRICE_RESPONSE');
    const at=now(),midnight=(Math.floor((at+28800000)/86400000)+1)*86400000-28800000;
    const deadlines=value.offers.map(o=>o&&o.expiresAt);
    if(deadlines.some(d=>!Number.isSafeInteger(d)||d<=at))throw error('INVALID_PRICE_OFFER');
    const until=Math.min(at+60000,midnight,...deadlines,job.selection.activityId?activity.endAt:Infinity);
    const display=copy(value);display.offers=display.offers.map(o=>({...o,expiresAt:Math.min(o.expiresAt,until)}));
    cache.set(job.key,{value:display,until});job.resolve(copy(display));
   }).catch(job.reject).finally(()=>{running--;if(jobs.get(job.key)===job)jobs.delete(job.key);drain();});
  }
 }
 function offers(selection){
  if(disposed)return Promise.reject(error('PRICE_PRELOAD_DISPOSED'));
  // A hub may receive newer authoritative progress before the next background
  // cycle. It must call setActivity with that progress before a limited read.
  if(!allowed(selection))return Promise.reject(error('PRICE_SELECTION_NOT_CURRENT'));
  const found=validEntry(selection);if(found)return Promise.resolve(copy(found.value));
  const k=key(selection);if(jobs.has(k))return jobs.get(k).promise;
  if(jobs.size>=5)return Promise.reject(error('PRICE_PRELOAD_QUEUE_FULL'));
  let resolve,reject;const promise=new Promise((a,b)=>{resolve=a;reject=b;});
  const job={key:k,selection:{skuId:selection.skuId,...(selection.activityId?{activityId:selection.activityId}:{})},promise,resolve,reject};
  jobs.set(k,job);queue.push(job);drain();return promise;
 }
 function schedule(){
  cancel();if(disposed||hidden()||!started)return;
  const at=now(),deadlines=[...cache.values()].map(x=>x.until).filter(x=>x>at);
  // Never tight-loop failed/expired offers. Visible hubs can explicitly retry.
  const delay=deadlines.length?Math.max(1000,Math.min(...deadlines)-at):30000;
  timer=setTimer(()=>{timer=null;warm();},Math.min(60000,delay));
 }
 function warm(){
  if(disposed||hidden()||!started)return Promise.resolve();
  if(cycle)return cycle;
  cancel();
  cycle=(async()=>{
   const permanent=PERMANENT.map(s=>offers(s).catch(()=>null));
   if(typeof readActivity==='function'){
    try{const next=await readActivity();if(!disposed)setActivity(next);}catch(_){if(!disposed)setActivity(null);}
   }
   if(!disposed&&!hidden()&&activity)await offers({skuId:'limited-step-'+activity.nextStep,activityId:activity.activityId}).catch(()=>null);
   await Promise.all(permanent);
  })().finally(()=>{cycle=null;schedule();});
  return cycle;
 }
 function visibility(){cancel();if(!hidden()){drain();warm();}}
 function start(){if(disposed||started)return;started=true;host?.document?.addEventListener?.('visibilitychange',visibility);warm();}
 function dispose(){disposed=true;started=false;cancel();host?.document?.removeEventListener?.('visibilitychange',visibility);cache.clear();activity=null;for(const job of queue.splice(0)){jobs.delete(job.key);job.reject(error('PRICE_PRELOAD_DISPOSED'));}}
 return Object.freeze({start,offers,setActivity,dispose});
}
module.exports={createPricePreload};
